import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import apiClient from "../api/apiClient";

const INTERVAL = 4500;

const FALLBACK_SLIDES = [
  {
    img: IMAGES.heroPhoto1,
    label: "STRENGTH",
    headline: ["BUILD", "STRENGTH"],
    message: "Every rep, every set — progress is built in the details. Show up and do the work.",
  },
  {
    img: IMAGES.heroPhoto2,
    label: "POWER",
    headline: ["UNLEASH", "YOUR POWER"],
    message: "Explosive, intentional, unstoppable. Power isn't given — it's earned through consistency.",
  },
  {
    img: IMAGES.heroPhoto3,
    label: "PERFORMANCE",
    headline: ["ELEVATE", "PERFORMANCE"],
    message: "Train with purpose. Every session is an opportunity to become a better version of yourself.",
  },
  {
    img: IMAGES.heroPhoto4,
    label: "DISCIPLINE",
    headline: ["STAY", "DISCIPLINED"],
    message: "Champions aren't made on their best days. They're built in the quiet moments of commitment.",
  },
];

const Home2Banner = ({ open }) => {
  const [slides, setSlides] = useState(FALLBACK_SLIDES);

  useEffect(() => {
    apiClient.get("/homepage/hero-slides")
      .then(({ data }) => {
        if (data && data.length > 0) {
          setSlides(data.map((s) => ({
            img: s.image_url,
            label: s.label,
            headline: [s.headline_1, s.headline_2],
            message: s.message,
          })));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((index) => {
    if (animating || index === current) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 700);
  }, [animating, current]);

  const slideCount = slides.length;
  useEffect(() => {
    const t = setInterval(() => {
      setCurrent((c) => (c + 1) % slideCount);
    }, INTERVAL);
    return () => clearInterval(t);
  }, [slideCount]);

  return (
    <div className="hero-banner">

      {/* ── Mobile: full-bleed background image ── */}
      <div className="hero-bg-mobile">
        {slides.map((slide, i) => (
          <img
            key={i}
            src={slide.img}
            alt={slide.label}
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center top",
              opacity: i === current ? 1 : 0,
              transition: "opacity 0.8s ease",
            }}
          />
        ))}
        <div className="hero-bg-overlay" />
      </div>

      {/* ── Desktop: right-side photo panel ── */}
      <div className="hero-photo-panel">
        {slides.map((slide, i) => (
          <img
            key={i}
            src={slide.img}
            alt={slide.label}
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center top",
              opacity: i === current ? 1 : 0,
              transition: "opacity 0.8s ease",
            }}
          />
        ))}
        <div className="hero-photo-fade-left" />
        <div className="hero-photo-fade-bottom" />
        <div className="hero-photo-glow" />
      </div>

      {/* ── Text content ── */}
      <div className="hero-content-wrap">
        <div className="container">
          <div className="hero-content-inner">

            <div key={`label-${current}`} className="hero-label">
              {slides[current].label}
            </div>

            <h1 key={`headline-${current}`} className="hero-headline">
              <span>{slides[current].headline[0]}</span>
              <span className="hero-headline-accent">{slides[current].headline[1]}</span>
            </h1>

            <p key={`msg-${current}`} className="hero-message">
              {slides[current].message}
            </p>

            <div className="hero-ctas">
              <Link to="/libraries" className="btn btn-primary btn-skew btn-lg">
                <span>Explore Libraries</span>
              </Link>
              <Link to="/#about" className="btn btn-skew btn-lg hero-btn-outline">
                <span>Learn More</span>
              </Link>
            </div>

            <div className="hero-dots">
              {slides.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} className={`hero-dot${i === current ? " active" : ""}`} />
              ))}
              <span className="hero-counter">0{current + 1} / 0{slideCount}</span>
            </div>

          </div>
        </div>
      </div>

      {/* ── Vertical bar indicators (desktop only) ── */}
      <div className="hero-bars">
        {slides.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} className={`hero-bar${i === current ? " active" : ""}`} />
        ))}
      </div>

      <style>{`
        .hero-banner {
          position: relative;
          height: 100vh;
          min-height: 560px;
          background: #0d0d0d;
          overflow: hidden;
        }

        /* ── Mobile background (visible on all, hidden on md+) ── */
        .hero-bg-mobile {
          position: absolute;
          inset: 0;
          z-index: 1;
        }
        .hero-bg-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, rgba(13,13,13,0.85) 0%, rgba(13,13,13,0.6) 50%, rgba(13,13,13,0.3) 100%);
        }

        /* ── Desktop photo panel (right side) ── */
        .hero-photo-panel {
          display: none;
          position: absolute;
          right: 0; top: 0;
          width: 52%; height: 100%;
          z-index: 2;
        }
        .hero-photo-fade-left {
          position: absolute; inset: 0;
          background: linear-gradient(to right, #0d0d0d 0%, rgba(13,13,13,0.4) 30%, transparent 60%);
          z-index: 2;
        }
        .hero-photo-fade-bottom {
          position: absolute; bottom: 0; left: 0; right: 0; height: 30%;
          background: linear-gradient(to top, #0d0d0d 0%, transparent 100%);
          z-index: 2;
        }
        .hero-photo-glow {
          position: absolute; bottom: -10%; left: 15%; right: 15%; height: 50%;
          background: radial-gradient(ellipse at center, rgba(192,57,43,0.35) 0%, transparent 70%);
          z-index: 3;
        }

        /* ── Content wrapper ── */
        .hero-content-wrap {
          position: absolute;
          inset: 0;
          z-index: 4;
          display: flex;
          align-items: center;
          padding-top: 70px;
        }
        .hero-content-inner {
          max-width: 540px;
          padding: 20px 0;
        }

        /* ── Label ── */
        .hero-label {
          display: inline-block;
          background: var(--primary, #c0392b);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 4px;
          padding: 6px 18px;
          border-radius: 2px;
          margin-bottom: 18px;
          animation: fadeSlideUp 0.5s ease forwards;
        }

        /* ── Headline ── */
        .hero-headline {
          color: #fff;
          font-size: clamp(38px, 8vw, 84px);
          font-weight: 900;
          line-height: 1.0;
          margin-bottom: 18px;
          text-transform: uppercase;
          animation: fadeSlideUp 0.55s ease forwards;
        }
        .hero-headline span { display: block; }
        .hero-headline-accent { color: var(--primary, #c0392b); }

        /* ── Message ── */
        .hero-message {
          color: rgba(255,255,255,0.7);
          font-size: 15px;
          line-height: 1.85;
          margin-bottom: 32px;
          animation: fadeSlideUp 0.65s ease 0.08s both;
        }

        /* ── CTAs ── */
        .hero-ctas {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 36px;
        }
        .hero-btn-outline {
          border: 2px solid rgba(255,255,255,0.3) !important;
          color: #fff !important;
          background: transparent !important;
        }

        /* ── Dots ── */
        .hero-dots {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hero-dot {
          width: 10px; height: 4px;
          border-radius: 2px; border: none;
          background: rgba(255,255,255,0.25);
          cursor: pointer;
          transition: all 0.4s ease;
          padding: 0;
        }
        .hero-dot.active {
          width: 36px;
          background: var(--primary, #c0392b);
        }
        .hero-counter {
          color: rgba(255,255,255,0.3);
          font-size: 12px;
          letter-spacing: 2px;
          margin-left: 6px;
        }

        /* ── Vertical bars ── */
        .hero-bars {
          display: none;
          position: absolute;
          right: 20px; top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .hero-bar {
          width: 3px; height: 18px;
          border: none; border-radius: 2px;
          background: rgba(255,255,255,0.25);
          cursor: pointer; transition: all 0.4s ease; padding: 0;
        }
        .hero-bar.active {
          height: 44px;
          background: var(--primary, #c0392b);
        }

        /* ── Breakpoints ── */
        @media (min-width: 768px) {
          .hero-bg-mobile { display: none; }
          .hero-photo-panel { display: block; }
          .hero-bars { display: flex; }
          .hero-content-inner { max-width: 480px; }
        }

        @media (max-width: 767px) {
          .hero-content-wrap { padding-top: 60px; align-items: center; }
          .hero-content-inner { max-width: 100%; padding: 16px 0; }
          .hero-message { font-size: 14px; }
          .hero-ctas { gap: 10px; }
          .hero-ctas .btn { font-size: 13px; padding: 10px 18px; }
          .hero-bars { display: none !important; }
        }

        @media (max-width: 400px) {
          .hero-banner { min-height: 100svh; }
          .hero-headline { font-size: clamp(34px, 10vw, 52px); }
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Home2Banner;
